import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Menu,
  X,
  Video,
  Monitor,
  Users,
  Map as MapIcon,
  Fingerprint,
  ArrowRight,
  MessageCircle,
  Send,
  Check,
  Loader2,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp configuration — all CTAs route here
const WHATSAPP_NUMBER = "40721959788"; // 0721959788 in international format (RO +40)
const WHATSAPP_DEFAULT_MSG =
  "Salut M.I.B.! Sunt interesat(ă) să discutăm despre un proiect.";
const waLink = (msg = WHATSAPP_DEFAULT_MSG) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

// ---------- Hook: parallax based on scroll position ----------
// Returns the current scrollY value (px). Updates via rAF for smooth 60fps.
const useScrollY = () => {
  const [y, setY] = useState(0);
  useEffect(() => {
    let rafId = 0;
    const update = () => {
      setY(window.scrollY || window.pageYOffset || 0);
      rafId = 0;
    };
    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  return y;
};

// ---------- Animated counter (counts 0 → target when in view) ----------
const AnimatedCounter = ({ end, suffix = "", duration = 1800, decimals = 0, prefix = "" }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const triggered = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !triggered.current) {
          triggered.current = true;
          const start = performance.now();
          const animate = (now) => {
            const t = Math.min((now - start) / duration, 1);
            // Ease-out cubic for nice deceleration
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(end * eased);
            if (t < 1) requestAnimationFrame(animate);
            else setValue(end);
          };
          requestAnimationFrame(animate);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);

  const display =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString("ro-RO");

  return (
    <span ref={ref}>
      {prefix}
      {display}
      <span className="text-brand-gold">{suffix}</span>
    </span>
  );
};

// ---------- Magnetic Button wrapper (desktop only) ----------
const MagneticButton = ({ children, strength = 0.25, className = "" }) => {
  const ref = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Skip on touch / coarse pointer devices — no cursor to magnet
    if (!window.matchMedia || !window.matchMedia("(pointer: fine)").matches) return;
    const el = ref.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * strength;
      const dy = (e.clientY - cy) * strength;
      setTransform({ x: dx, y: dy });
    };
    const onLeave = () => setTransform({ x: 0, y: 0 });

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);

  return (
    <div
      ref={ref}
      className={`inline-block transition-transform duration-200 ease-out ${className}`}
      style={{
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }}
    >
      {children}
    </div>
  );
};

// ============================================================
// Analytics & Consent System
// ============================================================

const CONSENT_KEY = "mib_consent_v1";

// Read IDs from env (frontend .env). Missing IDs simply skip that script.
const GA4_ID = process.env.REACT_APP_GA4_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 180 days
    if (Date.now() - parsed.timestamp > 180 * 24 * 60 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
};

const setStoredConsent = (consent) => {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ ...consent, timestamp: Date.now() })
    );
  } catch {
    /* localStorage unavailable */
  }
};

// Load external script tag once (idempotent via window flag)
const loadScript = (src, flagName, attrs = {}) => {
  if (window[flagName]) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  window[flagName] = true;
};

const initGA4 = () => {
  if (!GA4_ID || window.__mib_ga4_loaded) return;
  window.__mib_ga4_loaded = true;
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`, "__mib_ga4_script");
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { anonymize_ip: true });
};

const initMetaPixel = () => {
  if (!META_PIXEL_ID || window.__mib_fbq_loaded) return;
  window.__mib_fbq_loaded = true;
  // Standard Meta Pixel install (compressed)
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
};

const initClarity = () => {
  if (!CLARITY_ID || window.__mib_clarity_loaded) return;
  window.__mib_clarity_loaded = true;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
};

// Custom event tracker — fires to all enabled platforms
const trackEvent = (name, params = {}) => {
  try {
    if (window.gtag) window.gtag("event", name, params);
    if (window.fbq) window.fbq("trackCustom", name, params);
    if (window.clarity) window.clarity("event", name);
  } catch (e) {
    // Fail silently — analytics should never break UX
  }
};

// Hook: apply consent → load enabled scripts
const useApplyConsent = (consent) => {
  useEffect(() => {
    if (!consent) return;
    if (consent.analytics) {
      initGA4();
      initClarity();
    }
    if (consent.marketing) {
      initMetaPixel();
    }
  }, [consent]);
};

// ---------- Cookie Banner ----------
const CookieBanner = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    if (!getStoredConsent()) setOpen(true);
  }, []);

  const save = (consent) => {
    setStoredConsent(consent);
    onChange(consent);
    setOpen(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () =>
    save({ necessary: true, analytics, marketing });

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-brand-black/95 backdrop-blur-md border-t border-brand-blue/30 text-white shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.5)]"
      role="dialog"
      aria-label="Acord cookies"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 py-5 md:py-6">
        {!showCustomize ? (
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-8">
            <div className="flex-1">
              <div className="text-[10px] font-bold tracking-[0.3em] text-brand-blue uppercase mb-2">
                Cookies & Confidențialitate
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Folosim cookies pentru a îmbunătăți experiența ta și pentru a analiza
                traficul. Cele necesare rulează mereu; pe celelalte le poți accepta sau
                refuza. Detalii în{" "}
                <a
                  href="#privacy"
                  className="text-brand-gold hover:underline underline-offset-2"
                >
                  Politica de confidențialitate
                </a>
                .
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto shrink-0">
              <button
                data-testid="cookie-customize"
                onClick={() => setShowCustomize(true)}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-300 hover:text-white border border-white/20 hover:border-white/40 transition-colors"
              >
                Personalizează
              </button>
              <button
                data-testid="cookie-reject"
                onClick={rejectAll}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-300 hover:text-white border border-white/20 hover:border-white/40 transition-colors"
              >
                Refuză
              </button>
              <button
                data-testid="cookie-accept"
                onClick={acceptAll}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] bg-brand-blue text-white hover:bg-brand-gold hover:text-brand-black transition-colors"
              >
                Acceptă toate
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] text-brand-blue uppercase mb-3">
              Setări detaliate
            </div>
            <div className="space-y-3 mb-5">
              <label className="flex items-start gap-3 cursor-not-allowed opacity-60">
                <input type="checkbox" checked disabled className="mt-1" />
                <div>
                  <div className="text-sm font-bold text-white">Necesare (mereu pornite)</div>
                  <div className="text-xs text-gray-400">
                    Permit funcționarea de bază a site-ului (form contact, sesiune).
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cookie-toggle-analytics"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 accent-brand-blue"
                />
                <div>
                  <div className="text-sm font-bold text-white">Analytics</div>
                  <div className="text-xs text-gray-400">
                    Google Analytics + Microsoft Clarity — vedem cum este folosit
                    site-ul, fără identificare personală.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cookie-toggle-marketing"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1 accent-brand-blue"
                />
                <div>
                  <div className="text-sm font-bold text-white">Marketing</div>
                  <div className="text-xs text-gray-400">
                    Meta Pixel — folosit pentru campanii relevante pe Facebook/Instagram.
                  </div>
                </div>
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowCustomize(false)}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-300 hover:text-white border border-white/20"
              >
                Înapoi
              </button>
              <button
                data-testid="cookie-save-custom"
                onClick={saveCustom}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] bg-brand-blue text-white hover:bg-brand-gold hover:text-brand-black transition-colors"
              >
                Salvează preferințele
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Sticky Mobile CTA ----------
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Show after scrolling past hero (~600px)
      const scrolled = window.scrollY > 600;
      // Hide when contact section is in view (avoid duplicate CTA)
      const ctaEl = document.getElementById("iluminare");
      const inContact = ctaEl
        ? ctaEl.getBoundingClientRect().top < window.innerHeight * 0.7
        : false;
      setVisible(scrolled && !inContact);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`md:hidden fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pt-2 transition-all duration-500 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
      }`}
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="bg-brand-black/95 backdrop-blur-md border border-white/10 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.5)] flex items-stretch gap-2 p-2 rounded-sm">
        <a
          data-testid="sticky-cta-whatsapp"
          href={waLink("Salut M.I.B.! Sunt pe site și vreau să discutăm.")}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("whatsapp_click", { location: "sticky_mobile" })}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3.5 bg-[#25D366] text-white text-xs font-bold uppercase tracking-[0.15em]"
        >
          <MessageCircle className="w-4 h-4 shrink-0" />
          WhatsApp acum
        </a>
        <a
          data-testid="sticky-cta-form"
          href="#iluminare"
          onClick={() => trackEvent("scroll_to_form", { location: "sticky_mobile" })}
          className="inline-flex items-center justify-center px-4 py-3.5 bg-white text-brand-black text-xs font-bold uppercase tracking-[0.15em]"
        >
          Formular
        </a>
      </div>
    </div>
  );
};

// ---------- FAQ Section ----------
const FAQ_ITEMS = [
  {
    q: "Cât costă o colaborare cu M.I.B.?",
    a: "Fiecare colaborare începe cu o discuție și un brief detaliat — fără două proiecte identice, fără două oferte identice. Construim pachete personalizate în funcție de obiectivele tale, industria în care activezi și volumul de livrabile de care ai nevoie. Discutăm gratuit primii pași pe WhatsApp și revenim cu o propunere clară, fără presiuni.",
  },
  {
    q: "Când o să văd primele rezultate concrete?",
    a: "Conținutul video viral poate genera tracțiune în 2-3 săptămâni. Strategia de brand și SEO au efect compus — primele semnale clare apar la 60-90 zile, iar tracțiunea reală în 4-6 luni. Suntem foarte transparenți: niciun \"viral peste noapte\" garantat, ci sisteme care livrează predictibil.",
  },
  {
    q: "Lucrați și cu afaceri mici sau doar cu branduri mari?",
    a: "Lucrăm cu afaceri serioase, indiferent de mărime, care văd brandingul ca o investiție pe termen lung, nu ca un cost. Ofertele noastre sunt direct proporționale cu materialele livrate și amploarea proiectului — un brand local primește un pachet diferit față de o rețea cu mai multe locații. Trimite-ne contextul tău și construim împreună soluția potrivită pentru momentul în care te afli.",
  },
  {
    q: "Pe ce industrii sunteți specializați?",
    a: "Avem playbook-uri și echipe pregătite pentru 6 nișe: Beauty & Fashion (peste 6 ani experiență), Automotive Premium, HoReCa, Real Estate, Influencer Marketing și Personal Branding pentru autoritate publică. Dacă industria ta nu e listată, vom verifica deschis dacă putem livra rezultate.",
  },
  {
    q: "Ce face M.I.B. diferit față de alte agenții?",
    a: "Trei lucruri: (1) Producție in-house — fără freelanceri întâmplători, control de calitate; (2) Strategie pe date reale, nu pe gusturi personale; (3) Specializare pe nișe — refuzăm clienții pentru care nu avem sistem dovedit. Asta înseamnă rezultate mai bune și relații mai lungi.",
  },
  {
    q: "Cum decurge o colaborare în primele 30 de zile?",
    a: "Ziua 1-7: analizăm afacerea, piața și oportunitățile de dezvoltare.\nZiua 8-14: construim strategia și planul de conținut pentru următoarele 90 de zile.\nZiua 15-30: producem și lansăm primele materiale, astfel încât să vezi progres concret încă din prima lună.\nDupă primele 30 de zile: continuăm cu execuție, optimizare și livrări constante pentru a susține creșterea pe termen lung.",
  },
  {
    q: "Lucrați doar în România sau și internațional?",
    a: "Sediul nostru de creație este în România, dar lucrăm cu clienți din întreaga Europă. Vorbim română, engleză și franceză. Pentru proiectele OOH avem rețea de tipografii și parteneri media pentru lansări locale, dar producția video și strategia funcționează la fel de bine remote.",
  },
  {
    q: "Cum se face plata? Ai contracte standard?",
    a: "Plata se face 50% la semnare + 50% la livrare pentru proiecte one-off. Pentru retainer-uri lunare, plata se face la început de lună. Acceptăm transfer bancar (IBAN) și putem emite facturi cu TVA. Toate colaborările au contract standard de servicii cu clauze de confidențialitate.",
  },
];

const FAQItem = ({ item, index, isOpen, onToggle }) => (
  <div
    data-testid={`faq-item-${index}`}
    className={`border-b border-white/10 transition-colors ${
      isOpen ? "bg-white/[0.02]" : ""
    }`}
  >
    <button
      data-testid={`faq-toggle-${index}`}
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
    >
      <div className="flex items-start gap-4 md:gap-6 flex-1">
        <span className="text-xs font-bold tracking-[0.25em] text-brand-blue mt-1 shrink-0 tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h4
          className={`text-base md:text-xl font-bold tracking-tight transition-colors ${
            isOpen ? "text-brand-gold" : "text-white group-hover:text-brand-gold"
          }`}
        >
          {item.q}
        </h4>
      </div>
      <span
        className={`shrink-0 w-7 h-7 md:w-8 md:h-8 border border-white/20 flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? "rotate-45 border-brand-gold bg-brand-gold/10"
            : "group-hover:border-brand-blue"
        }`}
        aria-hidden="true"
      >
        <span
          className={`block w-3 h-px ${isOpen ? "bg-brand-gold" : "bg-white"}`}
        />
        <span
          className={`block w-px h-3 -ml-[7px] transition-opacity ${
            isOpen ? "opacity-0" : "bg-white"
          }`}
        />
      </span>
    </button>
    <div
      className={`grid transition-all duration-500 ease-out ${
        isOpen
          ? "grid-rows-[1fr] opacity-100 pb-6 md:pb-8"
          : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">
        <p className="text-sm md:text-base text-gray-300 leading-relaxed font-light pl-10 md:pl-14 pr-8 max-w-3xl whitespace-pre-line">
          {item.a}
        </p>
      </div>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(0);

  // ---- Inject FAQPage Schema.org JSON-LD at runtime, generated from FAQ_ITEMS
  // This ensures the schema NEVER drifts from the on-page content (single source of truth).
  useEffect(() => {
    const SCRIPT_ID = "mib-faq-schema";
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": (typeof window !== "undefined" ? window.location.origin : "") + "/#faq",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          // Strip line breaks for the schema (Google prefers single-paragraph)
          text: item.a.replace(/\n+/g, " ").trim(),
        },
      })),
    };

    let el = document.getElementById(SCRIPT_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = SCRIPT_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);

    return () => {
      const node = document.getElementById(SCRIPT_ID);
      if (node) node.remove();
    };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-32 relative bg-brand-black z-10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16 reveal">
          <div className="max-w-2xl">
            <h2 className="text-[11px] sm:text-sm font-bold tracking-[0.25em] sm:tracking-[0.3em] text-brand-blue uppercase mb-3 md:mb-4">
              Întrebări Frecvente
            </h2>
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white mb-4">
              Ce vrei să știi înainte să începem.
            </h3>
            <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed">
              Răspunsuri directe la întrebările pe care ni le pun cel mai des
              antreprenorii. Dacă a ta nu e aici, scrie-ne pe WhatsApp.
            </p>
          </div>
          <a
            href={waLink("Salut M.I.B.! Am o întrebare care nu e în FAQ.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-cta-whatsapp"
            onClick={() => trackEvent("whatsapp_click", { location: "faq" })}
            className="inline-flex items-center gap-3 self-start px-5 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-brand-blue hover:border-brand-blue transition-colors shrink-0"
          >
            <MessageCircle className="w-4 h-4" />
            Întreabă pe WhatsApp
          </a>
        </div>
        <div className="reveal">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              index={i}
              isOpen={openIdx === i}
              onToggle={() => {
                const newIdx = openIdx === i ? -1 : i;
                setOpenIdx(newIdx);
                if (newIdx === i) trackEvent("faq_open", { question: item.q });
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Logo = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <polygon points="12,2 16.5,10 7.5,10" />
    <polygon points="6,12 10.5,20 1.5,20" />
    <polygon points="18,12 22.5,20 13.5,20" />
  </svg>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [light, setLight] = useState(false);

  useEffect(() => {
    const trigger = document.getElementById("light-trigger");
    if (!trigger) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setLight(entry.isIntersecting || entry.boundingClientRect.top <= 100);
        });
      },
      { rootMargin: "-100px 0px 0px 0px" }
    );
    obs.observe(trigger);
    return () => obs.disconnect();
  }, []);

  const navCls = light ? "nav-light" : "nav-dark";
  const btnCls = light ? "nav-btn-light" : "nav-btn-dark";

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-50 transition-all duration-500 backdrop-blur-md ${navCls}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center h-20 md:h-24">
          <a
            data-testid="brand-logo"
            href="#acasa"
            className="text-xl md:text-2xl font-bold tracking-widest flex items-center gap-2 group"
          >
            <Logo className="w-6 h-6 text-brand-blue group-hover:text-brand-gold transition-colors duration-300" />
            <span className="whitespace-nowrap">M.I.B.</span>
            <span className="hidden lg:inline text-xs font-medium tracking-[0.25em] opacity-60 ml-2">
              MASTERS IN BRANDING
            </span>
          </a>

          <div className="hidden md:flex space-x-10 items-center">
            <a
              data-testid="nav-link-viziune"
              href="#viziune"
              className="text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity"
            >
              Viziune
            </a>
            <a
              data-testid="nav-link-mestesug"
              href="#mestesug"
              className="text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity"
            >
              Măiestrie
            </a>
            <a
              data-testid="nav-link-transformare"
              href="#transformare"
              className="text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity"
            >
              Evoluție
            </a>
            <a
              data-testid="nav-cta-whatsapp"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "navbar" })}
              className={`px-8 py-3 rounded-none text-sm font-bold tracking-wide uppercase transition-all duration-300 ${btnCls} hover:scale-105`}
            >
              Inițiază Proiectul
            </a>
          </div>

          <div className="md:hidden flex items-center">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen((v) => !v)}
              className="focus:outline-none p-2"
              aria-label="Meniu mobil"
            >
              {open ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-brand-black text-white border-t border-white/10 absolute w-full"
        >
          <div className="px-6 py-8 space-y-6 flex flex-col">
            <a
              href="#viziune"
              onClick={() => setOpen(false)}
              className="text-lg tracking-widest uppercase"
            >
              Viziune
            </a>
            <a
              href="#mestesug"
              onClick={() => setOpen(false)}
              className="text-lg tracking-widest uppercase"
            >
              Măiestrie
            </a>
            <a
              href="#transformare"
              onClick={() => setOpen(false)}
              className="text-lg tracking-widest uppercase"
            >
              Evoluție
            </a>
            <a
              data-testid="mobile-cta-whatsapp"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setOpen(false);
                trackEvent("whatsapp_click", { location: "mobile_menu" });
              }}
              className="text-brand-gold text-lg tracking-widest uppercase font-bold inline-flex items-center gap-3"
            >
              <MessageCircle className="w-5 h-5" /> Contact WhatsApp
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => {
  const scrollY = useScrollY();
  return (
  <section
    id="acasa"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-black z-10"
  >
    <div className="absolute inset-0 bg-grid-dark opacity-40" />
    <div
      className="glow-orb-blue top-1/4 left-1/4"
      style={{ transform: `translate3d(0, ${scrollY * 0.35}px, 0)`, willChange: "transform" }}
    />
    <div
      className="glow-orb-blue bottom-0 right-1/4 opacity-50"
      style={{ transform: `translate3d(0, ${scrollY * -0.2}px, 0)`, willChange: "transform" }}
    />

    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 text-center relative z-10 reveal">
      <div className="flex justify-center mb-8 md:mb-12">
        <div className="w-px h-16 md:h-24 bg-gradient-to-b from-transparent to-brand-blue" />
      </div>

      <div className="flex flex-col items-center gap-4 md:gap-5 mb-8 md:mb-10">
        <Logo className="w-14 h-14 md:w-20 md:h-20 text-brand-blue drop-shadow-[0_0_20px_rgba(30,94,255,0.4)]" />
        <div className="flex flex-col items-center gap-1">
          <span className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-[0.15em] text-white">
            M<span className="text-brand-gold">.</span>I<span className="text-brand-gold">.</span>B<span className="text-brand-gold">.</span>
          </span>
          <span className="text-[11px] sm:text-xs md:text-sm font-bold tracking-[0.35em] sm:tracking-[0.45em] uppercase text-brand-blue">
            Masters In Branding
          </span>
        </div>
      </div>

      <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white mb-6 md:mb-8 leading-[1.05]">
        <span className="text-spotlight pb-2">
          Atenția este moneda secolului.
        </span>
        <br />
        <span className="text-brand-gold mt-2 block">
          Noi o transformăm în profit.
        </span>
      </h1>

      <p className="mt-6 md:mt-8 text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-light mb-10 md:mb-12">
        De la primul scroll pe TikTok până la decizia finală de cumpărare.
        Suntem agenția full-service care îți construiește brandul, îți domină
        nișa și îți transformă audiența în clienți care plătesc. Strategie pe
        date reale, producție vizuală impecabilă și execuție rapidă, toate sub
        același acoperiș.
      </p>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
        <MagneticButton strength={0.3}>
          <a
            data-testid="hero-cta-whatsapp"
            href={waLink(
              "Salut M.I.B.! Vreau să discutăm despre proiectul meu."
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex w-full sm:w-auto px-8 sm:px-10 py-5 bg-white text-brand-black text-xs sm:text-sm font-bold uppercase tracking-widest overflow-hidden hover:scale-105 transition-transform duration-300"
          >
            <div className="absolute inset-0 w-0 bg-brand-blue transition-all duration-[250ms] ease-out group-hover:w-full -z-10" />
            <span className="group-hover:text-white transition-colors duration-300 flex items-center justify-center gap-3 relative z-10">
              Hai să vorbim despre proiectul tău
              <ArrowRight className="w-4 h-4 shrink-0" />
            </span>
          </a>
        </MagneticButton>
      </div>
    </div>
  </section>
  );
};

const VisionSection = () => (
  <section
    id="viziune"
    data-testid="vision-section"
    className="py-32 relative bg-brand-black z-10"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex justify-center mb-16 reveal">
        <div className="w-4 h-4 border border-brand-blue rotate-45" />
      </div>

      <div className="max-w-4xl mx-auto text-center reveal">
        <h2 className="text-sm font-bold tracking-[0.3em] text-brand-blue uppercase mb-6">
          Unde Viziunea Prinde Formă
        </h2>
        <h3 className="text-3xl md:text-5xl font-light text-white leading-tight mb-8">
          Majoritatea afacerilor operează în întuneric digital. Noi aducem
          claritatea, structura și lumina necesară pentru a te impune ca lider
          absolut.
        </h3>
        <p className="text-gray-500 text-lg font-light">
          Transformarea nu este o întâmplare. Este o călătorie pe care o facem
          împreună.
        </p>
      </div>
    </div>
  </section>
);

// ---------- Marquee trust-strip (honest: industries + stats, no fake logos) ----------
const TrustMarquee = () => {
  const items = [
    { text: "BEAUTY · COSMETICĂ", accent: false },
    { text: "6+ ANI EXPERIENȚĂ", accent: true },
    { text: "FASHION · LIFESTYLE", accent: false },
    { text: "100+ CAMPANII LIVRATE", accent: true },
    { text: "AUTOMOTIVE PREMIUM", accent: false },
    { text: "PRODUCȚIE IN-HOUSE", accent: true },
    { text: "HORECA & HOSPITALITY", accent: false },
    { text: "5M+ VIEWS GENERATE", accent: true },
    { text: "REAL ESTATE", accent: false },
    { text: "STRATEGIE PE DATE", accent: true },
    { text: "INFLUENCER MARKETING", accent: false },
    { text: "MULTI-CANAL · OOH + DIGITAL", accent: true },
    { text: "AUTORITATE PUBLICĂ", accent: false },
    { text: "LIVRARE SĂPTĂMÂNALĂ", accent: true },
  ];
  // Duplicate for seamless loop
  const loop = [...items, ...items];

  return (
    <section
      data-testid="trust-marquee"
      aria-label="Industrii și capabilități"
      className="relative bg-brand-black border-y border-white/5 py-6 md:py-8 z-10"
    >
      <div className="marquee">
        <div className="marquee-track">
          {loop.map((it, i) => (
            <div
              key={i}
              className="flex items-center gap-6 md:gap-10 px-5 md:px-8 shrink-0"
            >
              <span
                className={`text-xs md:text-sm font-bold tracking-[0.3em] uppercase whitespace-nowrap ${
                  it.accent ? "text-brand-gold" : "text-white/70"
                }`}
              >
                {it.text}
              </span>
              <span
                className="w-1.5 h-1.5 rotate-45 shrink-0 bg-brand-blue"
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- Progress dots tied to the central axis ----------
const ProgressDots = () => {
  const sectionIds = ["acasa", "viziune", "mestesug", "transformare", "nise", "iluminare"];
  const labels = ["Acasă", "Viziune", "Măiestrie", "Evoluție", "Nișe", "Contact"];
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const viewportMid = window.innerHeight / 2;
      let current = 0;
      sectionIds.forEach((id, i) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportMid) current = i;
      });
      setActiveIdx(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // 6 dots distributed along the vertical viewport
  return (
    <div className="hidden md:block pointer-events-none" data-testid="progress-dots">
      {sectionIds.map((id, i) => {
        const topPct = 15 + i * (70 / (sectionIds.length - 1)); // from 15% to 85% of viewport
        const state =
          i === activeIdx ? "active" : i < activeIdx ? "passed" : "";
        return (
          <a
            key={id}
            href={`#${id}`}
            className={`axis-dot ${state}`}
            data-label={labels[i]}
            data-testid={`progress-dot-${id}`}
            aria-label={`Navighează la secțiunea ${labels[i]}`}
            style={{ top: `${topPct}vh`, pointerEvents: "auto" }}
          />
        );
      })}
    </div>
  );
};

const services = [
  {
    icon: Video,
    title: "1. Conținut Video & Foto:",
    subtitle: "Motorul Viralității",
    desc: "Știm exact ce face degetul să se oprească din scroll. Atenția se câștigă în primele 3 secunde, iar noi livrăm conținut video vertical nativ (Reels, TikTok, Shorts) gândit să spargă algoritmii.",
    span: "lg:col-span-6",
    accent: "blue",
  },
  {
    icon: Monitor,
    title: "2. Web Design:",
    subtitle: "Vitrina Digitală Care Vinde 24/7",
    desc: "Un site frumos pe care utilizatorul se pierde este un site inutil. Noi construim experiențe rapide, logice și optimizate clar pentru un singur lucru: conversia.",
    span: "lg:col-span-6",
    accent: "blue",
  },
  {
    icon: Users,
    title: "3. Social Media Management: Comunități Reale",
    subtitle: null,
    desc: "Administrăm prezența online adaptând mesajul pentru cine contează. Nu ghicim audiențele, ci le înțelegem.",
    span: "lg:col-start-3 lg:col-span-8",
    accent: "gold",
    horizontal: true,
  },
  {
    icon: MapIcon,
    title: "4. Producție Publicitară & OOH:",
    subtitle: "Impact Vizual Acolo Unde Contează",
    desc: "Campania ta nu se termină când utilizatorul închide telefonul. Aducem brandul tău în lumea reală, în formate care ies în evidență în peisajul urban.",
    span: "lg:col-span-6",
    accent: "blue",
  },
  {
    icon: Fingerprint,
    title: "5. Strategie & Branding:",
    subtitle: "ADN-ul Succesului Tău",
    desc: "Brandul nu este doar un logo; este ceea ce spun oamenii despre tine când nu ești în cameră.",
    span: "lg:col-span-6",
    accent: "blue",
  },
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const isGold = service.accent === "gold";
  const borderColor = isGold ? "border-brand-gold" : "border-brand-blue";
  const iconColor = isGold ? "text-brand-gold" : "text-white";

  if (service.horizontal) {
    return (
      <div
        data-testid={`service-card-${index}`}
        className={`craft-card p-6 sm:p-8 md:p-10 ${service.span} reveal border-brand-gold/30`}
      >
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center">
          <div
            className={`w-14 h-14 sm:w-16 sm:h-16 border ${borderColor} flex items-center justify-center shrink-0 rotate-45`}
          >
            <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor} -rotate-45`} />
          </div>
          <div>
            <h4 className="text-xl sm:text-2xl font-bold text-white mb-3 md:mb-4 tracking-tight">
              {service.title}
            </h4>
            <p className="text-gray-400 font-light mb-0 md:mb-6 text-sm sm:text-base">{service.desc}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`service-card-${index}`}
      className={`craft-card p-6 sm:p-8 md:p-10 ${service.span} reveal flex flex-col`}
    >
      <div
        className={`w-12 h-12 border ${borderColor} flex items-center justify-center mb-6 md:mb-8 rotate-45`}
      >
        <Icon className={`w-5 h-5 ${iconColor} -rotate-45`} />
      </div>
      <h4 className="text-xl sm:text-2xl font-bold text-white mb-3 md:mb-4 tracking-tight">
        {service.title}
        {service.subtitle && (
          <>
            <br />
            <span className="text-gray-400 font-light">{service.subtitle}</span>
          </>
        )}
      </h4>
      <p className="text-gray-400 font-light mb-0 md:mb-8 text-sm sm:text-base">{service.desc}</p>
    </div>
  );
};

const CraftSection = () => (
  <section
    id="mestesug"
    data-testid="craft-section"
    className="py-20 md:py-32 relative bg-gradient-to-b from-brand-black to-brand-midBlue z-10 overflow-hidden"
  >
    <div className="absolute inset-0 bg-grid-dark opacity-20" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative z-10">
      <div className="text-center mb-16 md:mb-24 reveal">
        <h2 className="text-[11px] sm:text-sm font-bold tracking-[0.25em] sm:tracking-[0.3em] text-brand-gold uppercase mb-3 md:mb-4">
          Instrumentele Creației
        </h2>
        <h3 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white tracking-tighter">
          Ce Facem Concret
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 sm:gap-6 lg:gap-8">
        {services.map((s, i) => (
          <ServiceCard key={i} service={s} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const MetricsSection = () => {
  const scrollY = useScrollY();
  // Parse "300" / "5M" etc into numeric target + suffix unit
  const metrics = [
    { num: 300, suffix: "%", label: "Creștere Medie Conversii" },
    { num: 5, suffix: "M+", label: "Atenție Captată (Views)" },
    { num: 0, suffix: "%", label: "Timp Pierdut" },
    { num: 100, suffix: "%", label: "Focus pe Obiectiv" },
  ];
  return (
    <section
      id="transformare"
      data-testid="metrics-section"
      className="py-20 md:py-32 relative bg-gradient-to-b from-brand-midBlue to-gray-200 z-10 transition-colors duration-1000 overflow-hidden"
    >
      <div
        className="glow-orb-gold top-0 left-1/2 transform -translate-x-1/2"
        style={{ transform: `translate3d(-50%, ${scrollY * -0.15}px, 0)`, willChange: "transform" }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative z-10 text-center reveal">
        <h2 className="text-[11px] sm:text-sm font-bold tracking-[0.25em] sm:tracking-[0.3em] text-brand-gold uppercase mb-12 md:mb-16">
          Metrica Progresului
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-0">
          {metrics.map((m, i) => (
            <div
              key={i}
              className={`p-4 sm:p-6 ${i < 3 ? "md:border-r border-white/20" : ""}`}
            >
              <div className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-2 tabular-nums">
                <AnimatedCounter end={m.num} suffix={m.suffix} duration={1800} />
              </div>
              <div className="text-[10px] sm:text-xs tracking-widest uppercase text-gray-300">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NichesSection = () => {
  const niches = [
    {
      img: "/niches/automotive.png",
      eyebrow: "01 / Automotive",
      title: "Showroom-uri & Dealeri Auto Premium",
      desc: "O mașină de 80.000€ nu se vinde cu poze făcute cu telefonul în parcare. Avem playbook-uri vizuale gata pentru lansări de modele, walk-around-uri cinematice și conținut care transformă curioși în programări la test drive.",
      tag: "Sistem pregătit",
    },
    {
      img: "/niches/public-authority.png",
      eyebrow: "02 / Public Authority",
      title: "Personal Branding & Autoritate Publică",
      desc: "Politicieni, lideri de opinie, manageri publici. Construim arhitectura mesajului tău public: apariții autentice pe social media, materiale de campanie care nu se uită, gestionarea reputației online. Discreție absolută, impact măsurabil.",
      tag: "Sistem pregătit",
    },
    {
      img: "/niches/horeca.png",
      eyebrow: "03 / HoReCa",
      title: "Restaurante, Hoteluri & Lifestyle",
      desc: "Atmosfera nu se transmite prin meniu. Foto food de studio, drone exterior, reels-uri verticale care fac vizitatorul să rezerve înainte să termine de scroll-at. Producție in-house, livrare săptămânală constantă.",
      tag: "Sistem pregătit",
    },
    {
      img: "/niches/real-estate.png",
      eyebrow: "04 / Real Estate",
      title: "Imobiliare Premium & Dezvoltatori",
      desc: "Vinzi un stil de viață, nu metri pătrați. Producție video aspirațională, tururi virtuale și landing pages care semnează rezervări. Sisteme testate pentru lansări de proiecte rezidențiale și pre-vânzare.",
      tag: "Sistem pregătit",
    },
    {
      img: "/niches/beauty-fashion.png",
      eyebrow: "05 / Beauty & Fashion",
      title: "Beauty, Cosmetică & Fashion",
      desc: "Peste 6 ani de experiență cu branduri din beauty și fashion — saloane, clinici estetice, designeri, boutique-uri și magazine cosmetice. Știm exact ce face produsul să strălucească pe Instagram, cum se filmează un before/after credibil și cum se construiește o comunitate fidelă care recomandă mai departe.",
      tag: "6+ ani experiență",
      highlight: true,
    },
    {
      img: "/niches/influencer-marketing.png",
      eyebrow: "06 / Influencer Marketing",
      title: "Campanii cu Creatori & Influenceri",
      desc: "Gestionăm campanii end-to-end cu creatori de conținut: selecție bazată pe audiență reală (nu followers fake), brief-uri care nu sufocă creativitatea, contracte clare, măsurători de impact pe vânzări reale. Rețea proprie de creatori în nișe cheie.",
      tag: "Sistem pregătit",
    },
  ];
  return (
    <section
      id="nise"
      data-testid="niches-section"
      className="py-20 md:py-32 relative bg-brand-lightGray text-brand-black z-10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-light opacity-50" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12 md:mb-20 reveal">
          <div className="max-w-3xl">
            <h2 className="text-[11px] sm:text-sm font-bold tracking-[0.25em] sm:tracking-[0.3em] text-brand-blue uppercase mb-3 md:mb-4">
              Arhitectura Succesului
            </h2>
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter mb-5">
              Nișe de Expertiză.
            </h3>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed">
              Nu lucrăm cu oricine. Avem playbook-uri, echipe și sisteme construite
              special pentru câteva nișe pe care le cunoaștem la milimetru.
              Dacă afacerea ta se regăsește mai jos, plecăm cu un avans serios
              față de orice agenție generalistă.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {niches.map((n, i) => (
            <div
              key={i}
              data-testid={`niche-item-${i}`}
              className="group reveal cursor-default"
            >
              <div className="overflow-hidden bg-brand-black relative aspect-[4/3] mb-5 md:mb-6">
                <picture>
                  <source srcSet={n.img.replace(".png", ".webp")} type="image/webp" />
                  <img
                    src={n.img}
                    alt={n.title}
                    width="800"
                    height="600"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                </picture>
                <div className="absolute inset-0 border border-black/5 m-4 pointer-events-none transition-all duration-500 group-hover:m-2 group-hover:border-brand-blue/40" />
                <div
                  className={`absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm text-[10px] font-bold tracking-[0.2em] uppercase ${
                    n.highlight
                      ? "bg-brand-gold text-brand-black"
                      : "bg-white/90 text-brand-black"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                      n.highlight ? "bg-brand-black" : "bg-brand-blue"
                    }`}
                  />
                  {n.tag}
                </div>
              </div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-blue mb-2">
                {n.eyebrow}
              </div>
              <h4 className="text-xl sm:text-2xl font-bold mb-3 tracking-tight">
                {n.title}
              </h4>
              <p className="text-sm sm:text-base text-gray-700 font-light leading-relaxed">
                {n.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-14 md:mt-20 pt-10 border-t border-black/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 reveal">
          <p className="text-sm md:text-base text-gray-600 font-light max-w-2xl">
            <strong className="font-bold text-brand-black">Industria ta nu e listată?</strong>{" "}
            Înseamnă că ești fie prea devreme, fie suntem deja prinși cu un concurent.
            Scrie-ne și aflăm în 5 minute pe WhatsApp.
          </p>
          <a
            data-testid="niches-cta-whatsapp"
            href={waLink("Salut M.I.B.! Industria mea nu e listată — vreau să discutăm dacă putem lucra împreună.")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "niches" })}
            className="group inline-flex items-center gap-3 px-6 py-3 bg-brand-black text-white text-xs sm:text-sm font-bold uppercase tracking-[0.15em] hover:bg-brand-blue transition-colors duration-300 shrink-0"
          >
            Verifică Potrivirea
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    website: "", // honeypot
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [touched, setTouched] = useState({});
  // Attribution snapshot — captured once on mount from URL + sessionStorage
  const [attribution, setAttribution] = useState({});

  useEffect(() => {
    // Read current URL params
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_term: params.get("utm_term"),
    };
    const hasUrlUtm = Object.values(fromUrl).some(Boolean);

    let snapshot;
    if (hasUrlUtm) {
      // First-touch UTMs in this session — persist so refresh/scroll doesn't lose them
      snapshot = {
        ...fromUrl,
        referrer: document.referrer || null,
        landing_url: window.location.href,
      };
      try {
        sessionStorage.setItem("mib_attribution", JSON.stringify(snapshot));
      } catch (_) {
        // sessionStorage may be blocked (Safari private mode) — ignore
      }
    } else {
      // No fresh UTMs — try to use what we stored at first hit
      try {
        const stored = sessionStorage.getItem("mib_attribution");
        if (stored) {
          snapshot = JSON.parse(stored);
        }
      } catch (_) {
        /* sessionStorage unavailable — fall through to empty snapshot */
      }
      if (!snapshot) {
        snapshot = {
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
          utm_content: null,
          utm_term: null,
          referrer: document.referrer || null,
          landing_url: window.location.href,
        };
      }
    }
    setAttribution(snapshot);
  }, []);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onBlur = (field) => () =>
    setTouched((t) => ({ ...t, [field]: true }));

  const errors = {
    name: form.name.trim().length < 2 ? "Numele este obligatoriu (min 2 caractere)." : "",
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
      ? "Adresa de email pare invalidă."
      : "",
    message: form.message.trim().length < 10
      ? "Mesajul trebuie să aibă cel puțin 10 caractere."
      : "",
  };
  const hasErrors = errors.name || errors.email || errors.message;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (hasErrors) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim(),
        website: form.website || null,
        // Attribution (all optional)
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        utm_content: attribution.utm_content || null,
        utm_term: attribution.utm_term || null,
        referrer: attribution.referrer || null,
        landing_url: attribution.landing_url || null,
      });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", message: "", website: "" });
      setTouched({});
      trackEvent("form_submit", {
        source: attribution.utm_source || "direct",
        campaign: attribution.utm_campaign || null,
      });
      // Standard Meta conversion event
      try {
        if (window.fbq) window.fbq("track", "Lead");
      } catch (e) {/* noop */}
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string"
          ? detail
          : "Nu am putut trimite mesajul. Reîncearcă în câteva minute sau scrie-ne pe WhatsApp."
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success"
        className="max-w-2xl mx-auto bg-white border border-brand-blue/20 p-8 md:p-12 text-left shadow-[0_20px_60px_-20px_rgba(30,94,255,0.25)]"
      >
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-full bg-brand-blue text-white flex items-center justify-center">
            <Check className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-brand-blue">
              Mesaj livrat
            </div>
            <h4 className="text-xl md:text-2xl font-bold tracking-tight text-brand-black">
              Mulțumim! Te contactăm cât mai curând.
            </h4>
          </div>
        </div>
        <p className="text-sm md:text-base text-gray-600 font-light leading-relaxed mb-6">
          Ne uităm peste mesaj și revenim, de obicei în mai puțin de 24 de ore lucrătoare.
          Dacă e urgent, ne poți scrie direct pe WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setStatus("idle")}
            data-testid="contact-send-another"
            className="px-6 py-3 border border-brand-black text-brand-black text-xs font-bold uppercase tracking-[0.2em] hover:bg-brand-black hover:text-white transition-colors"
          >
            Trimite alt mesaj
          </button>
          <a
            href={waLink("Salut M.I.B.! Tocmai am trimis un mesaj prin formular.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-success-whatsapp"
            className="px-6 py-3 bg-[#25D366] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#1ebe57] transition-colors inline-flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            Sau pe WhatsApp
          </a>
        </div>
      </div>
    );
  }

  const inputBase =
    "w-full bg-white border border-black/15 px-4 py-3 text-sm md:text-base text-brand-black placeholder-gray-400 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 transition";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      noValidate
      className="max-w-2xl mx-auto text-left bg-white/60 backdrop-blur-md border border-black/10 p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
    >
      <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-brand-blue mb-3">
        Sau scrie-ne direct
      </div>
      <h4 className="text-2xl md:text-3xl font-bold tracking-tight text-brand-black mb-2">
        Formular de contact
      </h4>
      <p className="text-sm text-gray-600 font-light mb-6 md:mb-8">
        Răspundem de obicei în mai puțin de 24 de ore lucrătoare. Cu cât oferi
        mai mult context, cu atât revenim mai precis.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700 block mb-2">
            Nume <span className="text-brand-blue">*</span>
          </label>
          <input
            type="text"
            data-testid="contact-input-name"
            value={form.name}
            onChange={update("name")}
            onBlur={onBlur("name")}
            placeholder="Andrei Popescu"
            className={`${inputBase} ${touched.name && errors.name ? "border-red-500" : ""}`}
            autoComplete="name"
          />
          {touched.name && errors.name && (
            <div className="text-xs text-red-600 mt-1">{errors.name}</div>
          )}
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700 block mb-2">
            Email <span className="text-brand-blue">*</span>
          </label>
          <input
            type="email"
            data-testid="contact-input-email"
            value={form.email}
            onChange={update("email")}
            onBlur={onBlur("email")}
            placeholder="andrei@firma.ro"
            className={`${inputBase} ${touched.email && errors.email ? "border-red-500" : ""}`}
            autoComplete="email"
          />
          {touched.email && errors.email && (
            <div className="text-xs text-red-600 mt-1">{errors.email}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700 block mb-2">
            Telefon
          </label>
          <input
            type="tel"
            data-testid="contact-input-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="07xx xxx xxx"
            className={inputBase}
            autoComplete="tel"
          />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700 block mb-2">
            Companie / Brand
          </label>
          <input
            type="text"
            data-testid="contact-input-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Numele afacerii (opțional)"
            className={inputBase}
            autoComplete="organization"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700 block mb-2">
          Mesaj <span className="text-brand-blue">*</span>
        </label>
        <textarea
          rows={5}
          data-testid="contact-input-message"
          value={form.message}
          onChange={update("message")}
          onBlur={onBlur("message")}
          placeholder="Spune-ne câteva cuvinte despre proiect, nișa ta și obiective..."
          className={`${inputBase} resize-y ${
            touched.message && errors.message ? "border-red-500" : ""
          }`}
        />
        {touched.message && errors.message && (
          <div className="text-xs text-red-600 mt-1">{errors.message}</div>
        )}
      </div>

      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={update("website")}
        className="hidden"
        aria-hidden="true"
      />

      {status === "error" && (
        <div
          data-testid="contact-error"
          className="flex items-start gap-3 p-3 mb-4 bg-red-50 border border-red-200 text-red-800 text-sm"
        >
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <p className="text-[11px] text-gray-500 tracking-wide">
          Datele tale nu vor fi folosite pentru spam. Niciodată.
        </p>
        <button
          type="submit"
          data-testid="contact-submit"
          disabled={status === "sending"}
          className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-brand-black text-white text-xs font-bold uppercase tracking-[0.2em] overflow-hidden hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <span className="absolute inset-0 w-0 bg-brand-blue transition-all duration-500 group-hover:w-full" />
          <span className="relative flex items-center gap-3">
            {status === "sending" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Se trimite...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Trimite mesajul
              </>
            )}
          </span>
        </button>
      </div>
    </form>
  );
};

const CTASection = () => (
  <section
    id="iluminare"
    data-testid="cta-section"
    className="py-20 md:py-32 relative bg-brand-white text-brand-black z-10 overflow-hidden"
  >
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[1000px] max-h-[1000px] bg-gradient-radial from-brand-gold/10 via-brand-blue/5 to-transparent rounded-full opacity-70 pointer-events-none" />
    <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 relative z-10 text-center reveal">
      <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-6 md:mb-8 leading-[1.05]">
        Nu mai lăsa vizibilitatea pe mâna amatorilor.
      </h2>
      <p className="text-lg sm:text-xl md:text-2xl text-gray-600 font-light mb-10 md:mb-16 leading-relaxed">
        Ai nevoie de o echipă care știe să facă tranziția de la un Reel viral pe
        Instagram la o strategie clară de conversie și un panou pe bulevard.
        <br />
        <strong className="font-bold text-brand-black">
          M.I.B. este acea echipă.
        </strong>
      </p>

      <MagneticButton strength={0.25}>
        <a
          data-testid="final-cta-whatsapp"
          href={waLink(
            "Salut M.I.B.! Aș vrea să programăm o discuție despre proiectul meu."
          )}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("whatsapp_click", { location: "final_cta" })}
          className="group relative inline-flex w-full sm:w-auto px-8 sm:px-12 py-5 sm:py-6 bg-brand-black text-white text-xs sm:text-sm font-bold uppercase tracking-[0.15em] sm:tracking-[0.2em] overflow-hidden hover:scale-105 transition-all duration-500 shadow-[0_20px_50px_-10px_rgba(245,197,66,0.3)]"
        >
          <div className="absolute inset-0 w-0 bg-brand-gold transition-all duration-500 ease-in-out group-hover:w-full z-0" />
          <span className="relative z-10 group-hover:text-brand-black transition-colors duration-500 flex items-center justify-center gap-3 sm:gap-4 w-full">
            Programează o discuție pe WhatsApp
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
          </span>
        </a>
      </MagneticButton>

      <div className="flex items-center gap-4 my-12 md:my-16 max-w-md mx-auto">
        <div className="h-px flex-1 bg-black/15" />
        <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-gray-500">
          Sau prin formular
        </span>
        <div className="h-px flex-1 bg-black/15" />
      </div>
    </div>

    <div className="px-5 sm:px-6 lg:px-12 relative z-10">
      <ContactForm />
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="footer"
    className="py-10 md:py-12 bg-brand-white border-t border-black/10 text-brand-black text-center"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3 font-bold tracking-widest text-sm sm:text-base">
        <Logo className="w-5 h-5 text-brand-blue shrink-0" />
        <span className="text-center md:text-left">M.I.B. — MASTERS IN BRANDING</span>
      </div>
      <p className="text-xs sm:text-sm">
        &copy; {new Date().getFullYear()} Masters In Branding. Toate drepturile
        rezervate.
      </p>
    </div>
  </footer>
);

const FloatingWhatsApp = () => (
  <a
    data-testid="floating-whatsapp"
    href={waLink()}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Contact WhatsApp"
    onClick={() => trackEvent("whatsapp_click", { location: "floating" })}
    className="hidden md:flex fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white items-center justify-center shadow-[0_10px_30px_-5px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform duration-300"
  >
    <MessageCircle className="w-7 h-7" />
  </a>
);

const useScrollAxis = () => {
  useEffect(() => {
    const onScroll = () => {
      const axis = document.getElementById("central-axis");
      if (!axis) return;
      const scrollPx = document.documentElement.scrollTop;
      const winHeightPx =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      axis.style.height =
        Math.min((scrollPx / Math.max(winHeightPx, 1)) * 100, 100) + "%";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
};

const useReveal = () => {
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");
    const observers = [];
    elements.forEach((el) => {
      const obs = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          el.classList.add("active");
          obs.disconnect();
        }
      });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);
};

function App() {
  useScrollAxis();
  useReveal();
  const [consent, setConsent] = useState(getStoredConsent());
  useApplyConsent(consent);

  // Apply stored consent on mount (in case scripts need to load before banner closes)
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) setConsent(stored);
  }, []);

  return (
    <div className="App">
      <div id="central-axis" className="hidden md:block" />
      <ProgressDots />
      <Navbar />
      <Hero />
      <TrustMarquee />
      <VisionSection />
      <CraftSection />
      <MetricsSection />
      <div
        id="light-trigger"
        className="h-1 w-full bg-transparent absolute"
      />
      <NichesSection />
      <FAQSection />
      <CTASection />
      <Footer />
      <FloatingWhatsApp />
      <StickyMobileCTA />
      <CookieBanner onChange={setConsent} />
    </div>
  );
}

export default App;
